"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@/lib/utm/hooks";
import InfographicCard from "@/app/(main)/components/InfographicCard";
import type { Infographic } from "@/lib/infographics";
import { fetchInfographicsClient } from "@/lib/infographics";
import type { SystemCountry } from "@/lib/navigation";
import CountryFlag from "@/app/(main)/components/CountryFlag";
import EmptyState from "@/app/(main)/components/EmptyState";

interface InfographicsClientProps {
  initialInfographics: Infographic[];
  initialTotalPages: number;
  initialCount: number;
  initialFilterTypes: string[];
  /** All countries used in the system — backs the destination filter widget. */
  countries: SystemCountry[];
  /** Destination id resolved from the URL's ?country param (empty = none). */
  initialSelectedDestId?: string;
  pageSize?: number;
  sidebarExtra?: ReactNode;
}

export default function InfographicsClient({
  initialInfographics,
  initialTotalPages,
  initialCount,
  initialFilterTypes,
  countries,
  initialSelectedDestId = "",
  pageSize = 10,
  sidebarExtra,
}: InfographicsClientProps) {
  const [infographics, setInfographics] = useState(initialInfographics);
  const [filterTypes] = useState(initialFilterTypes);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialCount);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedDest, setSelectedDest] = useState(initialSelectedDestId);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const loadInfographics = useCallback(
    async (page: number, filter: string, destId: string) => {
      setLoading(true);
      try {
        const result = await fetchInfographicsClient(
          page,
          pageSize,
          filter || undefined,
          destId || undefined,
        );
        setInfographics(result.infographics);
        setTotalPages(result.totalPages);
        setTotalCount(result.count);
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    // The SSR render already reflects the initial page/filter/destination, so
    // skip the first client run to avoid a redundant refetch.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadInfographics(currentPage, selectedFilter, selectedDest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedFilter, selectedDest]);

  function handleFilterChange(filter: string) {
    setCurrentPage(1);
    setSelectedFilter(filter);
  }

  function handleDestinationChange(destId: string) {
    setCurrentPage(1);
    setSelectedDest(destId);

    // Reflect the selection in the URL's ?country param (the country name, to
    // match how destination pages link here), preserving any other params.
    const params = new URLSearchParams(searchParams.toString());
    const countryName = countries.find((c) => c.id === destId)?.name;
    if (countryName) params.set("country", countryName);
    else params.delete("country");
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div ref={containerRef}>
      <div className="row">
        {/* Main column */}
        <div className="col-xxl-8 col-lg-7">
          {totalCount > 0 && (
            <div className="d-flex align-items-center justify-content-between mb-4">
              <p className="blog-text mb-0">
                Showing{" "}
                <strong>
                  {startItem}–{endItem}
                </strong>{" "}
                of <strong>{totalCount}</strong> infographics
              </p>
              {totalPages > 1 && (
                <div className="d-flex gap-2">
                  <button
                    className="th-btn style4"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                    style={{ padding: "4px 10px" }}
                  >
                    <i className="fa fa-arrow-left"></i> Prev
                  </button>
                  <button
                    className="th-btn style4"
                    disabled={currentPage >= totalPages || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                    style={{ padding: "4px 10px" }}
                  >
                    Next <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Infographics grid */}
          <div
            className="row gy-4 gx-xl-4 g-3"
            style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}
          >
            {infographics.map((info) => (
              <div key={info.id} className="col-lg-6 col-md-6">
                <InfographicCard
                  title={info.title}
                  imageUrl={info.img}
                  pdfUrl={info.pdfUrl}
                  filterType={info.filterType || undefined}
                />
              </div>
            ))}

            {infographics.length === 0 && !loading && (
              <div className="col-12">
                {selectedDest || selectedFilter ? (
                  <EmptyState
                    icon="fa-regular fa-image"
                    title="No infographics match these filters"
                    text="We couldn't find any infographics for the selected filters. Try clearing them to browse all infographics."
                    action={
                      <button
                        type="button"
                        className="th-btn style1 th-icon"
                        onClick={() => {
                          handleDestinationChange("");
                          handleFilterChange("");
                        }}
                      >
                        Reset Filters
                      </button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon="fa-regular fa-image"
                    title="No infographics yet"
                    text="There are no infographics available at this time. Please check back soon."
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-xxl-4 col-lg-5">
          <aside className="sidebar-area position-sticky" style={{ top: 100 }}>
            {/* Category filter */}
            {filterTypes.length > 0 && (
              <div className="widget widget_categories">
                <h3 className="widget_title">Filter By Category</h3>
                <ul>
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange("");
                      }}
                      className={`pe-0 ${selectedFilter === "" ? "fw-bold text-theme" : ""}`}
                    >
                      <i className="far fa-image"></i>
                      All Infographics
                      {selectedFilter === "" && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                  {filterTypes.map((type) => (
                    <li key={type}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleFilterChange(type);
                        }}
                        className={`text-capitalize pe-0 ${selectedFilter === type ? "text-theme fw-bold" : ""}`}
                      >
                        <i className="far fa-globe"></i>
                        {type}
                        {selectedFilter === type && (
                          <i className="fa fa-check-circle lh-base float-end"></i>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Destination filter */}
            {countries.length > 0 && (
              <div className="widget widget_categories destination-filter-widget">
                <h3 className="widget_title">Filter By Destination</h3>
                <ul className="destination-filter-list">
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDestinationChange("");
                      }}
                      className={`pe-0 ${selectedDest === "" ? "fw-bold text-theme" : ""}`}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        width="25"
                        src="/main/img/theme-img/pin-mark.svg"
                        alt="Display all infographics"
                      />
                      All Destinations
                      {selectedDest === "" && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                  {countries.map((country) => (
                    <li key={country.id}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDestinationChange(country.id);
                        }}
                        className={`pe-0 ${selectedDest === country.id ? "text-theme fw-bold" : ""}`}
                      >
                        <CountryFlag
                          country={country.name}
                          fallback={
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              width="25"
                              src="/main/img/theme-img/pin-mark.svg"
                              alt={country.name}
                              style={{ marginRight: 6, verticalAlign: "middle" }}
                            />
                          }
                        />
                        {country.name}
                        {selectedDest === country.id && (
                          <i className="fa fa-check-circle lh-base float-end"></i>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sidebarExtra}
          </aside>
        </div>
      </div>

      <style>{`
        /* Move the widget's padding onto its title and list so the destination
           list's scrollbar can sit flush with the card edge. Scoped under
           .sidebar-area to beat the theme's ".widget_categories ul" rules. */
        .sidebar-area .destination-filter-widget {
          padding: 0;
        }
        .sidebar-area .destination-filter-widget .widget_title {
          padding-top: var(--widget-padding-y, 30px);
          padding-left: var(--widget-padding-x, 30px);
          padding-right: var(--widget-padding-x, 30px);
        }
        .sidebar-area .destination-filter-widget .widget_title:after {
          left: var(--widget-padding-x, 30px);
        }
        .sidebar-area .destination-filter-list {
          padding: 0 var(--widget-padding-x, 30px) var(--widget-padding-y, 30px);
          margin-bottom: 0;
        }

        /* Scrollable destination list — constrained only on desktop (≥1024px).
           The widget title stays fixed; only the list below it scrolls. */
        @media (min-width: 1024px) {
          .sidebar-area .destination-filter-list {
            max-height: 420px;
            overflow-y: auto;
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
          }
          .sidebar-area .destination-filter-list::-webkit-scrollbar {
            width: 6px;
          }
          .sidebar-area .destination-filter-list::-webkit-scrollbar-track {
            background: transparent;
          }
          .sidebar-area .destination-filter-list::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.18);
            border-radius: 999px;
          }
          .sidebar-area .destination-filter-list:hover::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.32);
          }
        }
      `}</style>
    </div>
  );
}
